import React, { useEffect, useState, type ChangeEvent } from 'react'
import SurveyPreparedQuestions from '../SurveyTempleteFeature/SurveyPreparedQuestions'
import SurveyCreateQuestions from '../SurveyTempleteFeature/SurveyCreateQuestions'
import SurveyItemDropdown from '../SurveyTempleteFeature/components/SurveyItemDropdown'
import { Box, Card, CardContent, LinearProgress, TextField, Typography } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { SurveyItemQuestionType } from '../../models/SurveyTemplate'
import { grey } from '../../colors'
import { ADDED_QUESTION_TYPES } from '../../consts/addedQuestionTypes'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { LookUpModal } from './LookUpModal'
import { getLookUpQuestion } from '../../APIManager/LookUpManager'
import { useQuery } from '@tanstack/react-query'
import { useAppDispatch } from '../../store'
import { getSurveySections } from '../../store/slices/SurveyTemplateSlice/selectors'
import { setSectionDescription, setSectionTitle, addSurveyQuestionInSection } from '../../store/slices/SurveyTemplateSlice'
import { type Question } from '../SurveyTempleteFeature/types'

export const AcademicUnitAndInstructorAddedQuestionsFeature: React.FC = () => {
  const [isCountComplete, setIsCountComplete] = React.useState<boolean>(false)
  const [isLookUpModalOpen, setIsLookUpModalOpen] = useState(false)
  const dispatch = useAppDispatch()
  const surveySectionsList = getSurveySections()
  const section = surveySectionsList?.[0]
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const type = params.get('type')

  const questionCount = params.get('questionCount')
  const categoryName = params.get('categoryName')
  const isQuestionAllowed = params.get('isInstructorAddedQuestionsAllowed')

  const { addedQuestions, totalQuestions, progress } = React.useMemo(() => {
    const added = section?.surveyItems?.filter(
      (item) => item.itemType !== SurveyItemQuestionType.TITLE
    ).length ?? 0
    const total = Number(questionCount)
    const prog = total > 0 ? (added / total) * 100 : 0
    return { addedQuestions: added, totalQuestions: total, progress: prog }
  }, [section?.surveyItems, questionCount])

  useEffect(() => {
    if (progress === 100) {
      setIsCountComplete(true)
    } else if (progress !== 100 && progress > 0) {
      setIsCountComplete(false)
    }
  }, [progress])

  const handleSectionTitleAndDescriptionChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    surveySectionId: string | undefined,
    setter: typeof setSectionTitle | typeof setSectionDescription
  ): void => {
    const { value } = event.target
    if (setter === setSectionTitle) {
      dispatch(setSectionTitle({ surveySectionId, value }))
    } else {
      dispatch(setSectionDescription({ surveySectionId, value }))
    }
  }

  const title = type === ADDED_QUESTION_TYPES.INSTRUCTOR ? 'Instructor Added Questions' : `${categoryName} Added Questions`

  // logic for checking for school-approved questions
  const { data: schoolApprovedQuestionIds = [], isLoading: loading } = useQuery<string[]>({
    queryKey: ['schoolApprovedQuestions'],
    queryFn: async () => {
      const res = await getLookUpQuestion({ school_approved: true })
      return res.data.map((question: Question) => question.itemId) || []
    },
    staleTime: 1000,
    retry: false
  })

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          width: 800,
          flexDirection: 'column',
          mx: 'auto',
          mt: 6
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display='flex' alignItems="center" justifyContent="space-between" >
            <Typography variant="subtitle1" fontWeight={500} fontSize={16} color={grey[250]}>
              {title}
            </Typography>
            {questionCount && Number(questionCount) > 0 &&
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    width: 60,
                    height: 8,
                    borderRadius: 5,
                    bgcolor: '#F3F4F6'
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {`${addedQuestions} out of ${totalQuestions} questions added`}
                </Typography>
              </Box>
            }
          </Box>

          <Typography>Section description (Optional)</Typography>
          <TextField
            sx={{ marginY: 2.5 }}
            fullWidth
            placeholder="e.g. A simple description that captures the content of this section."
            value={section?.descr ?? ''}
            onChange={e => { handleSectionTitleAndDescriptionChange(e, section?.surveySectionId, setSectionDescription) }}
          />
          <SurveyPreparedQuestions
            isAdminAddedQuestion={true}
            surveyItems={section.surveyItems}
            sectionIndex={0}
            section={section}
            isCountComplete={isCountComplete}
            arrayOfSchoolApprovedQuestionIds={schoolApprovedQuestionIds}
          />
          <SurveyCreateQuestions isAdminAddedQuestion={true} sectionIndex={0} section={section} isCountComplete={isCountComplete} />
          <Box display='flex' justifyContent='center' alignItems='center' gap={2}>
            <SurveyItemDropdown
              sectionNumber={0}
              isCountComplete={isCountComplete}
              isQuestionAllowed={
                isQuestionAllowed !== 'true'
              }
            />
            <Box mt={6}>
              <GenericButton
                buttonSize={ButtonSize.Medium}
                buttonType={ButtonType.Secondary}
                label='Lookup Question'
                startIcon={<SearchOutlinedIcon />}
                style={{ fontSize: '16px', fontWeight: 600, color: grey[950] }}
                onClick={() => { setIsLookUpModalOpen(true) }}
                disabled={!section?.surveySectionId}
              />
            </Box>
            {isLookUpModalOpen && (
              <LookUpModal
                lookUpData={{ selectedIds: section?.surveyItems?.map(item => item)?.filter(id => id != null) ?? [] }}
                onClose={() => { setIsLookUpModalOpen(false) }}
                totalQuestionLimit={Number(questionCount)}
                currentQuestionCount={addedQuestions}
                onAdd={({ removedQuestions, selectedQuestions }) => {
                  if (section?.surveySectionId) {
                    selectedQuestions.forEach(question => {
                      dispatch(addSurveyQuestionInSection({ surveySectionId: section.surveySectionId, question, removedQuestions }))
                    })
                  }
                  setIsLookUpModalOpen(false)
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </>
  )
}
